import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Divider,
  Rating,
  Chip,
} from '@mui/material'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone'
import CancelTwoToneIcon from '@mui/icons-material/CancelTwoTone'
// Use priority fires instead of star rating for reading-list
import SuppressionValidator from './BookPopUp'
import { DEFAULT_BOOK_IMAGE } from '../constants'

/**
 * Composant de dialogue affichant les détails d'un livre
 */
const BookDialog = ({
  open,
  onClose,
  book,
  cookies,
  setRefresh,
  onEdit,
  fullWidth = true,
  maxWidth = 'lg',
}) => {
  if (!book) return null

  return (
    <Dialog
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      open={open}
      onClose={onClose}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
    >
      <DialogTitle>
        <Typography sx={{ fontSize: 30 }} align="center">
          {book.title}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}
        >
          {/* Image du livre */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', m: { xs: 'auto', sm: '0' }, gap: 2 }}>
            <Box
              component="img"
              loading="lazy"
              decoding="async"
              sx={{
                maxHeight: { xs: 250, sm: 350, lg: 450 },
                maxWidth: { xs: 167, sm: 233, lg: 350 },
                boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                borderRadius: 2,
              }}
              alt={book.title}
              src={book.image_url || DEFAULT_BOOK_IMAGE}
            />
            {book.genre && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                <Typography component="legend" color="text.secondary" sx={{ mb: 1 }}>Genre</Typography>
                <Chip label={book.genre} color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Box>
            )}
            {book.note_5 !== null && book.note_5 !== undefined && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                <Typography component="legend" color="text.secondary" sx={{ mb: 1 }}>Note</Typography>
                <Rating
                  readOnly
                  value={Number(book.note_5) || 0}
                  icon={<FavoriteIcon fontSize="inherit" sx={{ color: '#F6658C' }} />}
                  emptyIcon={<FavoriteBorderIcon fontSize="inherit" />}
                  size="large"
                />
              </Box>
            )}
            {book.priority !== null && book.priority !== undefined && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                <Typography component="legend" color="text.secondary" sx={{ mb: 1 }}>Envie</Typography>
                <Rating
                  readOnly
                  value={Number(book.priority) || 0}
                  icon={<span style={{ fontSize: 'inherit' }}>🔥</span>}
                  emptyIcon={<span style={{ fontSize: 'inherit', opacity: 0.3, filter: 'grayscale(1)' }}>🔥</span>}
                  size="large"
                />
              </Box>
            )}
          </Box>

          {/* Informations du livre */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              m: 'auto',
              ml: 0,
              mt: 1,
              width: '100%',
              pl: { xs: 0, sm: 5 },
            }}
          >
            {/* Auteur et Éditeur */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              {book.author && (
                <TextField
                  fullWidth
                  label="Auteur"
                  variant="filled"
                  margin="normal"
                  InputProps={{ readOnly: true }}
                  value={book.author}
                  sx={{ mr: { xs: 0, sm: 1 } }}
                />
              )}
              {book.publisher && (
                <TextField
                  fullWidth
                  label="Éditeur"
                  variant="filled"
                  margin="normal"
                  InputProps={{ readOnly: true }}
                  value={book.publisher}
                  sx={{ ml: { xs: 0, sm: 1 } }}
                />
              )}
            </Box>

            {/* Date de publication */}
            {book.publish_date && (
              <DatePicker
                label="Date de publication"
                value={dayjs(book.publish_date)}
                readOnly
                slotProps={{
                  textField: {
                    variant: 'filled',
                    sx: { mt: 2, mb: 1 },
                  },
                }}
              />
            )}

            {/* Nombre de pages */}
            {book.pages && (
              <TextField
                fullWidth
                label="Pages"
                variant="filled"
                margin="normal"
                InputProps={{ readOnly: true }}
                type="number"
                value={book.pages}
              />
            )}

            {(book.summary || book.personnal_note || book.started_date || book.ended_date) && (
              <Divider sx={{ my: 2 }} />
            )}

            {/* Résumé */}
            {book.summary && (
              <TextField
                fullWidth
                label="Résumé"
                variant="filled"
                margin="normal"
                InputProps={{ readOnly: true }}
                value={book.summary}
                multiline
                rows={8}
              />
            )}

            {book.personnal_note && (
              <TextField
                fullWidth
                label="Note personnelle"
                variant="filled"
                margin="normal"
                InputProps={{ readOnly: true }}
                value={book.personnal_note}
                multiline
                rows={3}
              />
            )}

            {/* Dates de lecture */}
            {(book.started_date || book.ended_date) && (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                {book.started_date && (
                  <DatePicker
                    label="Date de début"
                    value={dayjs(book.started_date)}
                    readOnly
                    slotProps={{ textField: { variant: 'filled', sx: { mt: 2, mb: 1, mr: { xs: 0, sm: 1 }, width: '100%' } } }}
                  />
                )}
                {book.ended_date && (
                  <DatePicker
                    label="Date de fin"
                    value={dayjs(book.ended_date)}
                    readOnly
                    slotProps={{ textField: { variant: 'filled', sx: { mt: 2, mb: 1, ml: { xs: 0, sm: book.started_date ? 1 : 0 }, width: '100%' } } }}
                  />
                )}
              </Box>
            )}

            {(book.isbn_10 || book.isbn_13) && (
              <Divider sx={{ my: 2 }} />
            )}

            {/* ISBN */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              {book.isbn_10 && (
                <TextField
                  fullWidth
                  label="ISBN 10"
                  variant="filled"
                  margin="normal"
                  InputProps={{ readOnly: true }}
                  value={book.isbn_10}
                  sx={{ mr: { xs: 0, md: 1 } }}
                />
              )}
              {book.isbn_13 && (
                <TextField
                  fullWidth
                  label="ISBN 13"
                  variant="filled"
                  margin="normal"
                  InputProps={{ readOnly: true }}
                  value={book.isbn_13}
                  sx={{ ml: { xs: 0, md: 1 } }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onEdit} aria-label="Modifier le livre">
          <EditTwoToneIcon />
        </Button>

        <SuppressionValidator
          book_id={book.id}
          cookies={cookies}
          setRefresh={setRefresh}
          setOpenParent={onClose}
        />

        <Button onClick={onClose} aria-label="Fermer">
          <CancelTwoToneIcon />
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default BookDialog
